import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FileUsageComponent } from 'projects/apex/src/app/components/file-usage/file-usage.component';
import { t } from 'projects/apex/src/app/components/translate/translate.function';
import { CaseListEntity } from 'projects/apex/src/app/features/case/case.types';
import { snack, snackErr } from 'projects/apex/src/app/modules/snack.module';
import { Subscription, forkJoin, from, of } from 'rxjs';
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators';
import { MetaType } from '../../object.model';
import { AgreementRestService } from './agreement-rest.service';
import { Agreement } from './agreement.model';
import { AgreementService } from './agreement.service';

@Component({
  selector: 'apex-agreement',
  templateUrl: './form.component.html',
})
export class AgreementComponent implements OnInit, OnDestroy {
  @ViewChild('fileUsage') fu: FileUsageComponent;
  @ViewChild('fileUsagePublic') fuPublic: FileUsageComponent;

  agreement: Agreement;
  cases: CaseListEntity[] = [];

  MetaType = MetaType;

  navigateToId: number;
  projectId: number;

  private subscription = new Subscription();

  constructor(
    private service: AgreementService,
    private agreementRestService: AgreementRestService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.projectId = Number(this.route.snapshot.paramMap.get('pid'));
  }

  ngOnInit(): void {
    const sub = this.route.data
      .pipe(
        tap((d) => {
          this.agreement = d.agreement;
        }),
        switchMap((d) => {
          if (d.agreement?.id) {
            return from(this.agreementRestService.read(d.agreement.id)).pipe(catchError(() => of({ Cases: [] })));
          }

          return of({ Cases: [] });
        }),
      )
      .subscribe({
        next: (agreementWithCases) => {
          this.cases = (agreementWithCases.Cases as unknown as CaseListEntity[]) || [];
        },
      });

    this.subscription.add(sub);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  save(): void {
    const sub = this.service
      .saveByObject(this.agreement)
      .pipe(
        mergeMap((agreement) => {
          this.navigateToId = agreement.Entity?.id;
          this.fu.selfId = agreement.Entity?.id;
          this.fuPublic.selfId = agreement.Entity?.id;

          return forkJoin([this.fu.saveAll(), this.fuPublic.saveAll()]);
        }),
      )
      .subscribe({
        complete: () => {
          snack(t('Saved'));
          void this.router.navigate(this.whereTo());
        },
        error: (err) => snackErr(t('Could not save'), err),
      });

    this.subscription.add(sub);
  }

  delete(): void {
    if (this.agreement?.id) {
      const sub = this.service.deleteByObject(this.agreement).subscribe({
        next: (_) => {
          snack(t('Deleted'));
          void this.router.navigate(this.whereTo());
        },
        error: (err) => snackErr(t('Could not delete'), err),
      });

      this.subscription.add(sub);
    }
  }

  private whereTo(): string[] {
    let url = this.router.url;

    if (this.agreement?.id) {
      url = `object/project/${this.projectId}/agreement`;
    } else {
      url = url.replace('new', String(this.navigateToId));
    }

    return [url];
  }

  onTenantAdded(): void {
    snack(t('Tenant added as involved'));
  }
}
